// Address-driven price estimator API — POST /api/v1/units/estimate.
//
// Mirrors the backend contract in src/rentallens/api/units_estimate.py. The
// estimator is operator-only (no public landing page yet), so no mock layer
// here — the page will fail loudly when the backend is unreachable.

export type EstimateErrorReason =
  | 'address_unresolved'
  | 'no_apartment_at_dar'
  | 'out_of_scope';

export interface EstimateRequest {
  address: string;
  postal_code?: string | null;
  floor?: string | null;
  door?: string | null;
}

export interface ResolvedAddress {
  dar_id: string | null;
  address: string;
  postal_code: string;
  city: string;
  /** "exact" | "high" | "provisional" — DAWA match confidence. */
  confidence: string;
}

export interface UnitProfile {
  id: string;
  bbr_unit_id: string | null;
  area_m2: number | null;
  rooms: number | null;
  construction_year: number | null;
  energy_label: string | null;
  floor: string | null;
  door: string | null;
}

export interface EstimateBand {
  low_kr_per_month: number | null;
  suggested_kr_per_month: number | null;
  high_kr_per_month: number | null;
  low_kr_per_sqm: number | null;
  suggested_kr_per_sqm: number | null;
  high_kr_per_sqm: number | null;
  comparables_count: number;
  thin_sample: boolean;
}

export interface EstimateResponse {
  resolved: ResolvedAddress;
  unit: UnitProfile;
  estimate: EstimateBand;
}

/**
 * Structured error thrown when /estimate returns 4xx with a reason.
 * The route page branches on `reason` to render the right next-step.
 */
export class EstimateError extends Error {
  readonly reason: EstimateErrorReason | 'unknown';
  readonly status: number;

  constructor(message: string, status: number, reason: EstimateErrorReason | 'unknown') {
    super(message);
    this.name = 'EstimateError';
    this.status = status;
    this.reason = reason;
  }
}

export async function postEstimate(body: EstimateRequest): Promise<EstimateResponse> {
  const res = await fetch('/api/v1/units/estimate', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (res.status === 200) {
    return (await res.json()) as EstimateResponse;
  }
  // Try to extract the structured {detail, reason} payload. FastAPI nests it
  // under .detail when raised via HTTPException(detail={...}).
  let reason: EstimateErrorReason | 'unknown' = 'unknown';
  let detail = `estimate_failed_${res.status}`;
  try {
    const body = (await res.json()) as { detail?: { detail?: string; reason?: string } | string };
    if (typeof body.detail === 'object' && body.detail) {
      detail = body.detail.detail ?? detail;
      reason = (body.detail.reason as EstimateErrorReason) ?? 'unknown';
    } else if (typeof body.detail === 'string') {
      detail = body.detail;
    }
  } catch {
    // Body wasn't JSON — keep default messages.
  }
  throw new EstimateError(detail, res.status, reason);
}
